import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import type { LoadOptions } from "react-select-async-paginate";
import { useDispatch } from "react-redux";
import { Zap, Plus, X } from "lucide-react";
import { toast } from "react-hot-toast";
import ErrorModal from "../../../components/ErrorModal/ErrorModal";
import { RootDispatch } from "../../../store";
import {
  CreateTransferPayload,
  fetchAvailableProductionUnits,
} from "../../../store/slices/StockTransfer/stockTransferSlice";
import { IProductionUnit } from "../../../models/factory/productionUnit.model";

interface WarehouseOption {
  value: string;
  label: string;
}

interface ScannedItem {
  serial: string;
  productName: string;
  senderNote: string;
}

interface BatchSettings {
  fromWarehouse: WarehouseOption | null;
  toWarehouse: WarehouseOption | null;
  transferCost: number;
  handlingCost: number;
}

interface CreateTransferSectionProps {
  warehouseOptions: WarehouseOption[];
  isAdmin: boolean;
  warehouseAssigned: string | null;
  onSubmit: (payload: CreateTransferPayload) => Promise<void>;
  loading: boolean;
}

const CreateTransferSection: React.FC<CreateTransferSectionProps> = ({
  warehouseOptions,
  isAdmin,
  warehouseAssigned,
  onSubmit,
  loading,
}) => {
  const [batchSettings, setBatchSettings] = useState<BatchSettings>({
    fromWarehouse: null,
    toWarehouse: null,
    transferCost: 0,
    handlingCost: 0,
  });

  const [scannedItems, setScannedItems] = useState<ScannedItem[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<IProductionUnit | null>(
    null,
  );
  const [searchInput, setSearchInput] = useState("");
  const [currentNote, setCurrentNote] = useState("");
  const [scanLoading, setScanLoading] = useState(false);
  const [showUnavailableDialog, setShowUnavailableDialog] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  const dispatch: RootDispatch = useDispatch();

  useEffect(() => {
    if (!isAdmin || !warehouseAssigned) return;

    const assignedWarehouse = warehouseOptions.find(
      (warehouse) => warehouse.value === warehouseAssigned,
    );
    if (assignedWarehouse) {
      setBatchSettings((previous) =>
        previous.fromWarehouse
          ? previous
          : { ...previous, fromWarehouse: assignedWarehouse },
      );
    }
  }, [isAdmin, warehouseAssigned, warehouseOptions]);

  const loadProductionUnitOptions: LoadOptions<
    IProductionUnit,
    any,
    { page: number }
  > = async (search, _loadedOptions, additional) => {
    const page = additional?.page || 1;
    try {
      const response = await dispatch(
        fetchAvailableProductionUnits({
          params: { page, limit: 10, search: search.trim() },
        }),
      ).unwrap();
      const options = Array.isArray(response.data) ? response.data : [];

      return {
        options,
        hasMore: page < (response.pagination?.totalPages || 1),
        additional: { page: page + 1 },
      };
    } catch {
      return { options: [], hasMore: false, additional: { page } };
    }
  };

  const openTransferModal = () => setIsTransferModalOpen(true);

  const addUnit = (unit: IProductionUnit) => {
    const cleanSerial = unit.serialNumber?.trim();

    if (!cleanSerial) return toast.error("Select a production unit first");

    if (!/^\d{10}$/.test(cleanSerial)) {
      return toast.error("Serial number must be exactly 10 digits");
    }

    const isDuplicate = scannedItems.some(
      (item) => item.serial === cleanSerial,
    );

    if (isDuplicate) {
      toast.error("This serial number is already in the list!");
      setSelectedUnit(null);
      setSearchInput("");
      setCurrentNote("");
      return;
    }

    setScannedItems((prev) => [
      {
        serial: cleanSerial,
        productName: unit.productId?.productName || "Inverter",
        senderNote: currentNote,
      },
      ...prev,
    ]);

    setSelectedUnit(null);
    setSearchInput("");
    setCurrentNote("");
  };

  const handleScanKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const serial = searchInput.trim();
      if (!serial) return toast.error("Please enter a serial number");
      if (!/^\d{10}$/.test(serial)) {
        return toast.error("Serial number must be exactly 10 digits");
      }
      if (scannedItems.some((item) => item.serial === serial)) {
        toast.error("This serial number is already in the list!");
        setSearchInput("");
        setSelectedUnit(null);
        return;
      }

      setScanLoading(true);
      const result = await loadProductionUnitOptions(serial, [], { page: 1 });
      const matchingUnit = result.options.find(
        (unit) => unit.serialNumber?.trim() === serial,
      );
      setScanLoading(false);

      if (!matchingUnit) {
        setSelectedUnit(null);
        setSearchInput("");
        setShowUnavailableDialog(true);
        return;
      }

      addUnit(matchingUnit);
    }
  };

  // Helper to update remark directly in the table
  const handleRemarkChange = (index: number, newRemark: string) => {
    setScannedItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], senderNote: newRemark };
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (isAdmin && !batchSettings.fromWarehouse)
      return toast.error("Please select a Source Warehouse");
    if (!batchSettings.toWarehouse)
      return toast.error("Please select a Destination Warehouse");
    if (scannedItems.length === 0)
      return toast.error("Please scan at least one item");

    const payload: CreateTransferPayload = {
      ...(isAdmin && batchSettings.fromWarehouse
        ? { fromWarehouse: batchSettings.fromWarehouse.value }
        : {}),
      toWarehouse: batchSettings.toWarehouse.value,
      transferCost: batchSettings.transferCost,
      handlingCost: batchSettings.handlingCost,
      items: scannedItems.map((item) => ({
        serialNumber: item.serial,
        senderRemark: item.senderNote || undefined,
      })),
    };

    try {
      await onSubmit(payload);
      // Only clear on success
      setScannedItems([]);
      setBatchSettings((prev) => ({
        ...prev,
        transferCost: 0,
        handlingCost: 0,
      }));
    } catch (error) {
      console.error("Submission failed, keeping items.");
    }
  };

  return (
    <>
      <div className="w-full sm:w-auto">
        <button
          type="button"
          onClick={openTransferModal}
          className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#0077C0] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0069AA] focus:outline-none focus:ring-2 focus:ring-[#0077C0] focus:ring-offset-2 sm:w-auto sm:px-6 sm:text-base"
        >
          <Zap size={20} /> Start product transfer
        </button>
      </div>
      {isTransferModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[10000] flex items-end justify-center bg-slate-950/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setIsTransferModalOpen(false);
              }
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="stock-transfer-modal-title"
              className="relative max-h-[94vh] w-full max-w-6xl overflow-y-auto rounded-t-2xl bg-slate-100 p-3 shadow-2xl sm:rounded-xl sm:p-5"
            >
              <header className="sticky top-0 z-10 mb-3 flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <h2
                  id="stock-transfer-modal-title"
                  className="text-base font-semibold text-gray-900"
                >
                  Create Product Transfer
                </h2>
                <button
                  type="button"
                  aria-label="Close transfer dialog"
                  onClick={() => setIsTransferModalOpen(false)}
                  className="rounded-md p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
                >
                  <X size={20} />
                </button>
              </header>
              <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
                <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-800 sm:text-xl">
                  <Zap size={20} className="text-gray-700" /> Bulk Create
                  Product Transfers
                </h2>

                {/* --- Batch Settings --- */}
                <div className="mb-5 rounded-xl border border-gray-200 bg-gray-50 p-3 sm:mb-6 sm:p-4">
                  <h3 className="mb-3 text-sm font-semibold text-gray-800">
                    Product transfer details
                  </h3>
                  <div
                    className={`grid grid-cols-1 gap-4 ${
                      isAdmin
                        ? "md:grid-cols-2 lg:grid-cols-4"
                        : "md:grid-cols-3"
                    }`}
                  >
                    {isAdmin && (
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">
                          From Warehouse *
                        </label>
                        <Select
                          placeholder="Select source warehouse..."
                          value={batchSettings.fromWarehouse}
                          onChange={(option) =>
                            setBatchSettings((previous) => ({
                              ...previous,
                              fromWarehouse: option,
                            }))
                          }
                          options={warehouseOptions}
                          className="text-sm"
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 10010,
                            }),
                          }}
                        />
                      </div>
                    )}
                    <div className="md:col-span-1">
                      <label className="text-xs text-gray-600 block mb-1">
                        Destination Warehouse *
                      </label>
                      <Select
                        placeholder="Select Warehouse..."
                        value={batchSettings.toWarehouse}
                        onChange={(opt) =>
                          setBatchSettings((prev) => ({
                            ...prev,
                            toWarehouse: opt,
                          }))
                        }
                        options={warehouseOptions}
                        className="text-sm"
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 10010 }),
                        }}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">
                        Transfer Cost (Total)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={batchSettings.transferCost}
                        onChange={(e) =>
                          setBatchSettings((prev) => ({
                            ...prev,
                            transferCost: parseFloat(e.target.value) || 0,
                          }))
                        }
                        className="w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">
                        Handling Cost (Total)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={batchSettings.handlingCost}
                        onChange={(e) =>
                          setBatchSettings((prev) => ({
                            ...prev,
                            handlingCost: parseFloat(e.target.value) || 0,
                          }))
                        }
                        className="w-full px-3 py-2 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* --- Scanning Input Area --- */}
                <div className="mb-4 grid grid-cols-1 items-end gap-3 md:grid-cols-[minmax(0,1fr)_minmax(220px,0.65fr)_auto]">
                  <div className="w-full min-w-0">
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-1 block">
                      Search or Scan Inverter Serial (10 Digits)
                    </label>
                    <AsyncPaginate<
                      IProductionUnit,
                      any,
                      { page: number },
                      false
                    >
                      loadOptions={loadProductionUnitOptions}
                      additional={{ page: 1 }}
                      defaultOptions={false}
                      loadOptionsOnMenuOpen
                      debounceTimeout={250}
                      value={selectedUnit}
                      inputValue={searchInput}
                      onInputChange={(value, actionMeta) => {
                        if (actionMeta.action === "input-change")
                          setSearchInput(value);
                        return value;
                      }}
                      onChange={(unit) => {
                        setSelectedUnit(unit);
                        setSearchInput("");
                      }}
                      getOptionValue={(unit) => unit._id}
                      getOptionLabel={(unit) => unit.serialNumber}
                      formatOptionLabel={(unit) => (
                        <div>
                          <div className="font-medium">{unit.serialNumber}</div>
                          <div className="text-xs text-gray-500">
                            {unit.productId?.productName || "Inverter"} ·{" "}
                            {unit.warehouseId?.name || "Unknown warehouse"}
                          </div>
                        </div>
                      )}
                      onKeyDown={handleScanKeyDown}
                      isLoading={scanLoading}
                      placeholder="Search by serial or scan a 10-digit serial..."
                      noOptionsMessage={() => "No available inverters found"}
                      className="text-sm"
                      menuPortalTarget={document.body}
                      styles={{
                        control: (base) => ({ ...base, minHeight: 48 }),
                        menuPortal: (base) => ({ ...base, zIndex: 10010 }),
                      }}
                    />
                  </div>
                  <div className="w-full min-w-0">
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-1 block">
                      Remark (Optional)
                    </label>
                    <input
                      placeholder="Item specific remark..."
                      value={currentNote}
                      onChange={(e) => setCurrentNote(e.target.value)}
                      onKeyDown={handleScanKeyDown}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0077C0]"
                    />
                  </div>
                  <button
                    type="button"
                    aria-label="Add selected unit"
                    onClick={() => selectedUnit && addUnit(selectedUnit)}
                    disabled={!selectedUnit || scanLoading}
                    className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-slate-800 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50 md:w-auto"
                  >
                    <Plus size={20} />
                    <span className="md:hidden">Add item</span>
                  </button>
                </div>

                {/* --- Summary Table --- */}
                {scannedItems.length > 0 && (
                  <div className="mt-4 animate-fade-in overflow-hidden rounded-xl border border-blue-100 bg-blue-50">
                    <div className="flex items-center justify-between gap-3 border-b border-blue-200 bg-blue-100/50 p-3 sm:p-4">
                      <span className="text-sm font-semibold text-blue-900">
                        Items to Send ({scannedItems.length})
                      </span>
                      <button
                        type="button"
                        onClick={() => setScannedItems([])}
                        className="min-h-10 rounded-lg px-3 text-xs font-semibold text-red-600 hover:bg-white/70"
                      >
                        Clear All
                      </button>
                    </div>

                    <div className="hidden w-full overflow-x-auto sm:block">
                      <table className="w-full text-sm text-left">
                        <thead className="text-xs text-gray-500 uppercase bg-blue-50/50 sticky top-0">
                          <tr>
                            <th className="px-4 py-2">Serial</th>
                            <th className="px-4 py-2">Product</th>
                            <th className="px-4 py-2">Destination</th>
                            <th className="px-4 py-2">Remark</th>
                            <th className="px-4 py-2 text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scannedItems.map((item, idx) => (
                            <tr
                              key={idx}
                              className="border-b border-blue-100 last:border-0 hover:bg-blue-100/30"
                            >
                              <td className="px-4 py-2 font-medium text-gray-800">
                                {item.serial}
                              </td>
                              <td className="px-4 py-2 text-gray-700">
                                {item.productName}
                              </td>
                              <td className="px-4 py-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-white border border-blue-200 text-blue-700 shadow-sm">
                                  {batchSettings.toWarehouse?.label || (
                                    <span className="text-red-500">
                                      Not Selected
                                    </span>
                                  )}
                                </span>
                              </td>
                              {/* Direct Input for Editing Remarks */}
                              <td className="px-4 py-2">
                                <input
                                  value={item.senderNote}
                                  onChange={(e) =>
                                    handleRemarkChange(idx, e.target.value)
                                  }
                                  placeholder="Add/Edit remark..."
                                  className="w-full bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none px-1 py-1 text-gray-600 italic text-sm transition-colors"
                                />
                              </td>
                              <td className="px-4 py-2 text-right">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setScannedItems((prev) =>
                                      prev.filter((_, i) => i !== idx),
                                    )
                                  }
                                  aria-label={`Remove ${item.serial}`}
                                  className="flex min-h-10 min-w-10 items-center justify-center rounded-lg text-red-500 hover:bg-red-50 hover:text-red-700"
                                >
                                  <X size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="grid gap-3 p-3 sm:hidden">
                      {scannedItems.map((item, idx) => (
                        <article
                          key={item.serial}
                          className="rounded-lg border border-blue-100 bg-white p-3 shadow-sm"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-mono font-semibold text-gray-900">
                                {item.serial}
                              </p>
                              <p className="mt-1 truncate text-sm font-medium text-gray-800">
                                {item.productName}
                              </p>
                              <p className="mt-1 truncate text-xs text-blue-700">
                                {batchSettings.toWarehouse?.label ||
                                  "Destination not selected"}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setScannedItems((previous) =>
                                  previous.filter(
                                    (scannedItem) =>
                                      scannedItem.serial !== item.serial,
                                  ),
                                )
                              }
                              aria-label={`Remove ${item.serial}`}
                              className="flex min-h-10 min-w-10 shrink-0 items-center justify-center rounded-lg text-red-500 hover:bg-red-50 hover:text-red-700"
                            >
                              <X size={17} />
                            </button>
                          </div>
                          <label className="mt-3 block text-xs font-medium text-gray-600">
                            Remark
                            <input
                              value={item.senderNote}
                              onChange={(event) =>
                                handleRemarkChange(idx, event.target.value)
                              }
                              placeholder="Add an optional remark"
                              className="mt-1 min-h-10 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#0077C0] focus:outline-none focus:ring-2 focus:ring-blue-100"
                            />
                          </label>
                        </article>
                      ))}
                    </div>

                    <div className="flex justify-end border-t border-blue-200 p-3 sm:p-4">
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={loading}
                        className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-[#0077C0] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#0069AA] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:px-6"
                      >
                        {loading && (
                          <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        )}
                        Confirm & Create ({scannedItems.length})
                      </button>
                    </div>
                  </div>
                )}
                {showUnavailableDialog && (
                  <ErrorModal
                    message="Inverter not available."
                    onClose={() => setShowUnavailableDialog(false)}
                    showCancel={false}
                  />
                )}
              </section>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default CreateTransferSection;
